import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, input, inject } from '@angular/core';
import { injectMutation, injectQueryClient } from '@tanstack/angular-query-experimental';
import { Bucket } from '../../types';
import { DrawerService } from '../../services/drawer.service';
import { BucketService, toBucketUpdateDraft } from '../../services/bucket.service';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Platform } from '@angular/cdk/platform';
import { filter, debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgTemplateOutlet, CurrencyPipe } from '@angular/common';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { BucketProgressComponent } from '../../shared/bucket-progress/bucket-progress.component';
import { UiInputDirective } from '../../ui/input/input.directive';
import { CategoryBucketSummaryInfoComponent } from '../category-bucket-summary-info/category-bucket-summary-info.component';
import { BalanceAmountComponent } from '../../shared/balance-amount/balance-amount.component';
import { UiFieldComponent } from '../../ui/field/field.component';
import { BudgetBulkEditService } from '../../services/budget-bulk-edit.service';
import { UiIconButtonComponent } from '../../ui/icon-button/icon-button.component';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { UiDialogService } from '../../ui/dialog/dialog.service';
import {
  ArchiveBudgetDialogComponent,
  ArchiveBudgetDialogResult,
} from '../archive-budget-dialog/archive-budget-dialog.component';
import { BucketTypeOptionValue } from '../../buckets/bucket-type-options';
import { BucketTypeSelectorComponent } from '../../buckets/bucket-type-selector/bucket-type-selector.component';

@Component({
  selector: 'app-category-bucket-summary',
  templateUrl: './category-bucket-summary.component.html',
  styleUrl: './category-bucket-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiButtonDirective,
    NgTemplateOutlet,
    BucketProgressComponent,
    UiInputDirective,
    ReactiveFormsModule,
    FormsModule,
    CategoryBucketSummaryInfoComponent,
    BalanceAmountComponent,
    UiFieldComponent,
    CurrencyPipe,
    UiIconButtonComponent,
    UiIconComponent,
    BucketTypeSelectorComponent,
  ],
})
export class CategoryBucketSummaryComponent implements OnInit, OnDestroy {
  private drawer = inject(DrawerService);
  bucketService = inject(BucketService);
  platform = inject(Platform);
  bulkEdit = inject(BudgetBulkEditService);
  private dialog = inject(UiDialogService);

  readonly bucket = input.required<Bucket>();
  readonly reorder = input(false);
  readonly archiveCandidates = input<Bucket[]>([]);
  private readonly queryClient = injectQueryClient();
  readonly updateBucketMutation = injectMutation(() => this.bucketService.updateBucketMutationOptions());
  readonly deleteBucketMutation = injectMutation(() => this.bucketService.deleteBucketMutationOptions());
  formGroup = new FormGroup<{
    name: FormControl<string>;
    amount: FormControl<number | null>;
  }>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    amount: new FormControl(0, [Validators.required, Validators.pattern('[0-9.]*')]),
  });

  private readonly destroy$ = new Subject<void>();

  get unsignedSpent(): number {
    return Math.abs(this.bucket().total ?? 0);
  }

  bucketTypeValue(): BucketTypeOptionValue {
    return this.bucket().type as BucketTypeOptionValue;
  }

  ngOnInit() {
    const bucket = this.bucket();
    this.formGroup.setValue({ amount: bucket.amount, name: bucket.name });
    this.formGroup.markAllAsTouched();
    this.formGroup.valueChanges
      .pipe(
        filter(() => this.formGroup.valid),
        debounceTime(200),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.updateBucket(this.formGroup.getRawValue()));
  }

  openDrawer(bucket: Bucket) {
    this.drawer.openBucketDrawer(bucket.id);
  }

  openTransferDrawer(bucket: Bucket) {
    this.drawer.openBucketDrawer(bucket.id, 'exchange');
  }

  async deleteBucket(bucket: Bucket): Promise<void> {
    await this.deleteBucketMutation.mutateAsync(bucket.id);
  }

  updateBucket({ amount, name }: { amount: number | null; name: string }) {
    const bucket = this.bucket();
    if (bucket._pendingCreate) {
      this.bulkEdit.updatePendingBucket(bucket.id, { amount, name });
      return;
    }
    if (this.reorder()) {
      this.bulkEdit.updateBucket(bucket, { amount, name });
      return;
    }
    if (bucket.name === name && bucket.amount === amount) return;

    bucket.name = name;
    bucket.amount = amount ?? 0;

    this.updateBucketMutation.mutate(
      toBucketUpdateDraft(bucket, {
        name: bucket.name,
        amount: bucket.amount,
      }),
    );
  }

  updateReorderName(name: string): void {
    const bucket = this.bucket();
    if (bucket._pendingCreate) this.bulkEdit.updatePendingBucket(bucket.id, { name });
    else this.bulkEdit.updateBucket(bucket, { name });
  }

  updateReorderAmount(amount: number | null): void {
    const bucket = this.bucket();
    if (bucket._pendingCreate) this.bulkEdit.updatePendingBucket(bucket.id, { amount });
    else this.bulkEdit.updateBucket(bucket, { amount });
  }

  updatePendingType(type: BucketTypeOptionValue): void {
    const bucket = this.bucket();
    bucket.type = type;
    if (bucket._pendingCreate) this.bulkEdit.updatePendingBucket(bucket.id, { type });
  }

  removePendingBucket(bucket: Bucket): void {
    this.bulkEdit.removePendingBucket(bucket.id);
  }

  async openArchiveDialog(bucket: Bucket): Promise<void> {
    if (this.bulkEdit.isPendingArchive(bucket.id)) {
      this.bulkEdit.undoArchive(bucket.id);
      return;
    }
    if (this.bulkEdit.isPendingUnarchive(bucket.id)) {
      this.bulkEdit.undoUnarchive(bucket.id);
      return;
    }
    if (bucket.archivedMonth) {
      this.bulkEdit.markUnarchive(bucket.id);
      return;
    }

    const { balance } = await this.queryClient.fetchQuery(
      this.bucketService.archiveBucketTransferredBalanceQueryOptions(bucket.id),
    );
    if (balance === 0) {
      this.bulkEdit.markArchive(bucket.id);
      return;
    }

    const candidates = this.archiveCandidates().filter(candidate => candidate.id !== bucket.id);
    const dialogRef = this.dialog.open<ArchiveBudgetDialogComponent, unknown, ArchiveBudgetDialogResult | undefined>(
      ArchiveBudgetDialogComponent,
      {
        data: {
          bucket,
          candidates,
          balance,
        },
      },
    );

    dialogRef.closed.subscribe(result => {
      if (!result) return;
      this.bulkEdit.markArchive(bucket.id, result.transferBalanceBucketId);
    });
  }

  async submitSetupBucket(): Promise<void> {
    if (this.formGroup.invalid) return;

    const { name, amount } = this.formGroup.getRawValue();
    const bucket = this.bucket();
    bucket.name = name;
    bucket.amount = amount ?? 0;
    bucket.isSetup = true;
    await this.updateBucketMutation.mutateAsync(
      toBucketUpdateDraft(bucket, {
        name,
        amount: amount ?? 0,
        isSetup: true,
      }),
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
